/**
 * Captura de midia local: microfone, camera e tela.
 *
 * Todo o cuidado com 1080p60 comeca aqui: o pedido de captura leva
 * `frameRate: { ideal, max }`, e logo depois da captura repetimos as restricoes
 * com `applyConstraints` — o Chrome costuma entregar 30 fps na primeira
 * negociacao com o compositor e so sobe para 60 quando o pedido e repetido.
 */

import {
  QUALITY_PRESETS,
  type ContentHintMode,
  type QualityId,
  type QualityPreset,
} from "@shared/protocol";
import { applyNoiseSuppression } from "@/media/noiseSuppression";
import { startNativeSystemAudio } from "@/media/nativeSystemAudio";
import type { CaptureResult } from "@/media/types";

/** De onde veio o pedido de midia; define a mensagem de erro mostrada. */
export type MediaSource = "mic" | "cam" | "screen";

/** `latency` existe no Chrome mas nao na tipagem padrao do DOM. */
type MicConstraints = MediaTrackConstraints & { latency?: number };

/** Opcoes de captura de tela ainda ausentes da tipagem do DOM. */
type DisplayOptions = DisplayMediaStreamOptions & {
  preferCurrentTab?: boolean;
  selfBrowserSurface?: "include" | "exclude";
  surfaceSwitching?: "include" | "exclude";
  systemAudio?: "include" | "exclude";
  monitorTypeSurfaces?: "include" | "exclude";
};

/** Voz humana: o codec pode usar DTX e banda estreita sem prejuizo. */
export const MIC_CONTENT_HINT = "speech";

/** Audio da transmissao: musica/jogo, sem cortes agressivos. */
export const SCREEN_AUDIO_CONTENT_HINT = "music";

/** A camera e sempre conteudo em movimento. */
export const CAM_CONTENT_HINT = "motion";

const INSECURE_CONTEXT =
  "O navegador só libera microfone, câmera e tela em conexões seguras (HTTPS) ou em localhost.";

const MIC_ERRORS: Record<string, string> = {
  NotAllowedError:
    "Permissão negada para usar o microfone. Libere o acesso nas configurações do navegador.",
  PermissionDeniedError:
    "Permissão negada para usar o microfone. Libere o acesso nas configurações do navegador.",
  NotFoundError: "Nenhum microfone foi encontrado neste dispositivo.",
  DevicesNotFoundError: "Nenhum microfone foi encontrado neste dispositivo.",
  NotReadableError: "O microfone está em uso por outro aplicativo.",
  TrackStartError: "O microfone está em uso por outro aplicativo.",
  OverconstrainedError: "Nenhum microfone atende à configuração pedida.",
  AbortError: "A captura do microfone foi interrompida pelo sistema.",
  SecurityError: "O navegador bloqueou o acesso ao microfone.",
};

const CAM_ERRORS: Record<string, string> = {
  NotAllowedError:
    "Permissão negada para usar a câmera. Libere o acesso nas configurações do navegador.",
  PermissionDeniedError:
    "Permissão negada para usar a câmera. Libere o acesso nas configurações do navegador.",
  NotFoundError: "Nenhuma câmera foi encontrada neste dispositivo.",
  DevicesNotFoundError: "Nenhuma câmera foi encontrada neste dispositivo.",
  NotReadableError: "A câmera está em uso por outro aplicativo.",
  TrackStartError: "A câmera está em uso por outro aplicativo.",
  OverconstrainedError: "A qualidade escolhida não é suportada por esta câmera.",
  AbortError: "A captura da câmera foi interrompida pelo sistema.",
  SecurityError: "O navegador bloqueou o acesso à câmera.",
};

const SCREEN_ERRORS: Record<string, string> = {
  NotAllowedError: "Transmissão cancelada: o navegador não liberou a captura de tela.",
  PermissionDeniedError: "Transmissão cancelada: o navegador não liberou a captura de tela.",
  NotFoundError: "Nenhuma tela disponível para transmitir.",
  NotReadableError: "Não foi possível ler a tela escolhida.",
  OverconstrainedError: "A qualidade escolhida não é suportada por esta fonte de captura.",
  AbortError: "A captura de tela foi interrompida pelo sistema.",
  SecurityError: "O navegador bloqueou a captura de tela.",
};

const FALLBACK_ERRORS: Record<MediaSource, string> = {
  mic: "Não foi possível acessar o microfone.",
  cam: "Não foi possível acessar a câmera.",
  screen: "Não foi possível iniciar a transmissão de tela.",
};

const TABLES: Record<MediaSource, Record<string, string>> = {
  mic: MIC_ERRORS,
  cam: CAM_ERRORS,
  screen: SCREEN_ERRORS,
};

/** Traduz o erro do navegador para uma frase util em portugues. */
export function mediaErrorMessage(error: unknown, source: MediaSource): string {
  const name = error instanceof Error ? error.name : "";
  return TABLES[source][name] ?? FALLBACK_ERRORS[source];
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : "";
}

function isOverconstrained(error: unknown): boolean {
  const name = errorName(error);
  return name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError";
}

function mediaDevices(): MediaDevices {
  const devices: MediaDevices | undefined = navigator.mediaDevices;
  if (!devices || typeof devices.getUserMedia !== "function") throw new Error(INSECURE_CONTEXT);
  return devices;
}

export function presetOf(quality: QualityId): QualityPreset {
  return QUALITY_PRESETS[quality];
}

/**
 * Restricoes de video do preset. `frameRate` leva `ideal` **e** `max`: sem o
 * `max` o Chrome trata 60 fps como sugestao e devolve 30 na maioria das telas.
 */
export function videoConstraints(preset: QualityPreset): MediaTrackConstraints {
  const constraints: MediaTrackConstraints = {};
  if (preset.width !== null) constraints.width = { ideal: preset.width };
  if (preset.height !== null) constraints.height = { ideal: preset.height };
  if (preset.frameRate !== null) {
    constraints.frameRate = { ideal: preset.frameRate, max: preset.frameRate };
  }
  return constraints;
}

/** Reaplica o preset numa trilha ja capturada. Devolve `false` se o dispositivo recusou. */
export async function applyVideoQuality(
  track: MediaStreamTrack,
  preset: QualityPreset,
): Promise<boolean> {
  try {
    await track.applyConstraints(videoConstraints(preset));
    return true;
  } catch {
    return false;
  }
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) track.stop();
}

/** Tenta o pedido exato e, se as restricoes forem recusadas, cai para o pedido simples. */
async function request(
  exact: () => Promise<MediaStream>,
  loose: () => Promise<MediaStream>,
  source: MediaSource,
): Promise<MediaStream> {
  try {
    return await exact();
  } catch (error) {
    if (!isOverconstrained(error)) throw new Error(mediaErrorMessage(error, source));
    try {
      return await loose();
    } catch (retry) {
      throw new Error(mediaErrorMessage(retry, source));
    }
  }
}

export type MicCapture = {
  stream: MediaStream;
  dispose: () => void;
};

/**
 * `noiseSuppression` aqui e a supressao por IA (GTCRN/WASM), em cima da
 * `noiseSuppression` nativa do navegador nas constraints abaixo — a nativa
 * fica sempre ligada (e leve e ja ajuda), a por IA e a camada extra que da
 * o resultado tipo Krisp, e pode ser desligada por quem preferir menos CPU
 * ou notar artefato na voz.
 */
export async function getMicStream(
  noiseSuppression = true,
  /**
   * Chamado quando o contexto de audio do microfone morre de vez.
   *
   * Nao e erro de captura: a trilha continua "viva" e apenas emite silencio,
   * entao ninguem descobre sozinho. Quem chama usa isto para refazer o
   * microfone do zero.
   */
  aoPerderOContexto?: () => void,
): Promise<MicCapture> {
  const devices = mediaDevices();
  const audio: MicConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    sampleRate: 48_000,
    latency: 0.01,
  };
  const rawStream = await request(
    () => devices.getUserMedia({ audio, video: false }),
    () => devices.getUserMedia({ audio: true, video: false }),
    "mic",
  );

  if (noiseSuppression) {
    const processed = await applyNoiseSuppression(rawStream, aoPerderOContexto);
    if (processed) {
      for (const track of processed.stream.getAudioTracks()) track.contentHint = MIC_CONTENT_HINT;
      return {
        stream: processed.stream,
        dispose: () => {
          processed.dispose();
          stopStream(rawStream);
        },
      };
    }
  }

  for (const track of rawStream.getAudioTracks()) track.contentHint = MIC_CONTENT_HINT;
  return { stream: rawStream, dispose: () => stopStream(rawStream) };
}

export async function getCamStream(quality: QualityId): Promise<MediaStream> {
  const devices = mediaDevices();
  const preset = presetOf(quality);
  const video: MediaTrackConstraints = { ...videoConstraints(preset), facingMode: "user" };
  const stream = await request(
    () => devices.getUserMedia({ video, audio: false }),
    () => devices.getUserMedia({ video: true, audio: false }),
    "cam",
  );
  const track = stream.getVideoTracks()[0];
  if (track) {
    await applyVideoQuality(track, preset);
    track.contentHint = CAM_CONTENT_HINT;
  }
  return stream;
}

/**
 * Uma dimensao "bate" com o pedido se ficar a 10% dela — captura de tela
 * nunca é exata (o SO arredonda para a resolução real do monitor), então
 * exigir o valor cravado marcaria como "falhou" toda transmissão saudável.
 */
function withinTolerance(got: number, wanted: number | null): boolean {
  if (wanted === null || wanted <= 0) return true;
  return got >= wanted * 0.9;
}

/**
 * Mede o que a fonte realmente entregou, para comparar com o que foi pedido.
 * `frameRate` de `getSettings()` costuma vir ausente ou desatualizado logo
 * após abrir a captura — por isso a leitura acontece de novo depois de um
 * intervalo curto, quando o primeiro quadro já passou pelo pipeline.
 */
async function measureCapture(
  track: MediaStreamTrack,
  preset: QualityPreset,
  systemAudioSource: CaptureResult["systemAudioSource"],
): Promise<CaptureResult> {
  await new Promise(resolve => setTimeout(resolve, 400));
  const settings = track.getSettings();
  const width = settings.width ?? 0;
  const height = settings.height ?? 0;
  const frameRate = Math.round(settings.frameRate ?? 0);
  const matchesRequest = withinTolerance(height, preset.height) && withinTolerance(frameRate, preset.frameRate);
  return { width, height, frameRate, matchesRequest, systemAudioSource };
}

export async function getScreenStream(
  quality: QualityId,
  hint: ContentHintMode = "motion",
  includeSystemAudio = false,
): Promise<{ stream: MediaStream; capture: CaptureResult }> {
  const devices = mediaDevices();
  if (typeof devices.getDisplayMedia !== "function") {
    throw new Error("Este navegador não permite transmitir a tela.");
  }
  const preset = presetOf(quality);
  // No app desktop, o audio do sistema vem separado (ver abaixo) capturado
  // nativamente excluindo o proprio LFRCORD — nao pedimos ele aqui pro
  // getDisplayMedia, que so tem o modo "tudo ou nada" e vazaria a call.
  const useNativeAudio = includeSystemAudio && typeof window !== "undefined" && Boolean(window.lfrcordDesktop);
  const wantsBrowserAudio = includeSystemAudio && !useNativeAudio;
  const options: DisplayOptions = {
    video: videoConstraints(preset),
    // Áudio do sistema é OPCIONAL e desligado por padrão: incluído, ele capta
    // qualquer coisa que o próprio LFRCORD esteja tocando (a voz dos outros
    // participantes saindo pelo alto-falante), o que soa como eco para quem
    // recebe a transmissão. Só entra quando o membro pede explicitamente.
    audio: wantsBrowserAudio
      ? {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 2,
          sampleRate: 48_000,
        }
      : false,
    preferCurrentTab: false,
    selfBrowserSurface: "exclude",
    surfaceSwitching: "include",
    systemAudio: wantsBrowserAudio ? "include" : "exclude",
    monitorTypeSurfaces: "include",
  };

  let stream: MediaStream;
  try {
    stream = await devices.getDisplayMedia(options);
  } catch (error) {
    throw new Error(mediaErrorMessage(error, "screen"));
  }

  const video = stream.getVideoTracks()[0];
  if (!video) {
    stopStream(stream);
    throw new Error("A captura de tela não trouxe nenhuma trilha de vídeo.");
  }
  await applyVideoQuality(video, preset);
  video.contentHint = hint;

  let systemAudioSource: CaptureResult["systemAudioSource"] = wantsBrowserAudio ? "browser" : "none";
  if (useNativeAudio) {
    const native = await startNativeSystemAudio();
    if (native) {
      stream.addTrack(native.track);
      const stopNative = (): void => native.stop();
      native.track.addEventListener("ended", stopNative, { once: true });
      video.addEventListener("ended", stopNative, { once: true });
      systemAudioSource = "native";
    }
  }
  for (const track of stream.getAudioTracks()) track.contentHint = SCREEN_AUDIO_CONTENT_HINT;

  const capture = await measureCapture(video, preset, systemAudioSource);
  return { stream, capture };
}
